import React, { useState, useEffect } from 'react';
import type { SuggestionReport } from '../types/interfaces';
import { sendReportEmail, type EmailReportData } from '../utils/emailService';

interface SuggestionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SUGGESTIONS_STORAGE_KEY = 'cryptoscopeSuggestions';

export default function SuggestionsModal({ isOpen, onClose }: SuggestionsModalProps) {
  const [reportType, setReportType] = useState<'bug' | 'suggestion'>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [copied, setCopied] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [viewMode, setViewMode] = useState<'create' | 'view'>('create');
  const [suggestions, setSuggestions] = useState<SuggestionReport[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadSuggestions();
    }
  }, [isOpen]);

  const loadSuggestions = () => {
    const saved = localStorage.getItem(SUGGESTIONS_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSuggestions(Array.isArray(parsed) ? parsed : []);
      } catch {
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
    }
  };

  const saveSuggestion = (report: Omit<SuggestionReport, 'id' | 'timestamp'>) => {
    const newReport: SuggestionReport = {
      ...report,
      id: `report-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
      status: 'pending'
    };

    const updated = [newReport, ...suggestions];
    localStorage.setItem(SUGGESTIONS_STORAGE_KEY, JSON.stringify(updated));
    setSuggestions(updated);
    return newReport;
  };

  const deleteSuggestion = (id: string) => {
    if (confirm('Are you sure you want to delete this report?')) {
      const updated = suggestions.filter(s => s.id !== id);
      localStorage.setItem(SUGGESTIONS_STORAGE_KEY, JSON.stringify(updated));
      setSuggestions(updated);
    }
  };

  const handleCopyToClipboard = async (report?: SuggestionReport) => {
    const reportToCopy = report || createReportObject();
    if (!reportToCopy) {
      alert('Please fill in at least the title and description.');
      return;
    }

    const formattedReport = formatReport(reportToCopy);
    
    try {
      await navigator.clipboard.writeText(formattedReport);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = formattedReport;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        alert('Failed to copy to clipboard. Please copy the text manually.');
      }
      document.body.removeChild(textarea);
    }
  };

  const createReportObject = (): Omit<SuggestionReport, 'id' | 'timestamp'> | null => {
    if (!title.trim() || !description.trim()) {
      return null;
    }

    return {
      type: reportType,
      title: title.trim(),
      description: description.trim(),
      steps: steps.trim() || undefined,
      browser: navigator.userAgent,
      platform: navigator.platform
    };
  };

  const formatReport = (report: SuggestionReport | Omit<SuggestionReport, 'id' | 'timestamp'>): string => {
    const typeLabel = report.type === 'bug' ? '🐛 Bug Report' : '💡 Feature Suggestion';
    let formatted = `${typeLabel}\n`;
    formatted += `${'='.repeat(50)}\n\n`;
    formatted += `**Title:** ${report.title}\n\n`;
    formatted += `**Description:**\n${report.description}\n\n`;
    
    if (report.type === 'bug' && report.steps) {
      formatted += `**Steps to Reproduce:**\n${report.steps}\n\n`;
    }
    
    formatted += `---\n`;
    formatted += `**Browser:** ${report.browser}\n`;
    formatted += `**Platform:** ${report.platform}\n`;
    
    if ('timestamp' in report && report.timestamp) {
      formatted += `**Timestamp:** ${new Date(report.timestamp).toISOString()}\n`;
    }
    
    return formatted;
  };

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      alert('Please fill in at least the title and description.');
      return;
    }

    const reportData = createReportObject();
    if (!reportData) return;

    setSubmitting(true);
    setSubmitError(null);

    // Save locally first
    const savedReport = saveSuggestion(reportData);

    // Prepare email data
    const emailData: EmailReportData = {
      type: reportType,
      title: title.trim(),
      description: description.trim(),
      steps: steps.trim() || undefined,
      browser: navigator.userAgent,
      platform: navigator.platform,
      timestamp: new Date().toISOString()
    };

    // Send email
    const emailSent = await sendReportEmail(emailData);

    setSubmitting(false);

    if (emailSent) {
      setSubmitted(true);
      setTimeout(() => {
        handleReset();
        setViewMode('view');
      }, 2000);
    } else {
      setSubmitError('Failed to send email, but report was saved locally. Please check your email service configuration.');
    }
  };

  const handleReset = () => {
    setTitle('');
    setDescription('');
    setSteps('');
    setReportType('bug');
    setCopied(false);
    setSubmitted(false);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] bg-black/70 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-10 max-w-2xl w-[90%] max-h-[90vh] overflow-y-auto relative shadow-bubble-lg border-2 border-primary/20"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-8 top-8 w-12 h-12 rounded-full bg-bg border-2 border-border text-3xl font-bold text-text-secondary cursor-pointer transition-all duration-300 hover:bg-danger hover:border-danger hover:text-white hover:scale-110 hover:rotate-90 active:scale-95 flex items-center justify-center"
        >
          &times;
        </button>

        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setViewMode('create')}
            className={`px-6 py-3 rounded-full border-2 transition-all duration-300 font-semibold ${
              viewMode === 'create'
                ? 'bg-gradient-to-r from-primary to-purple-500 border-primary text-white shadow-bubble-lg scale-105'
                : 'bg-transparent border-border text-text hover:border-primary hover:scale-105'
            }`}
          >
            ✏️ Create Report
          </button>
          <button
            onClick={() => {
              setViewMode('view');
              loadSuggestions();
            }}
            className={`px-6 py-3 rounded-full border-2 transition-all duration-300 font-semibold ${
              viewMode === 'view'
                ? 'bg-gradient-to-r from-primary to-purple-500 border-primary text-white shadow-bubble-lg scale-105'
                : 'bg-transparent border-border text-text hover:border-primary hover:scale-105'
            }`}
          >
            📋 View Reports ({suggestions.length})
          </button>
        </div>

        <h2 className="text-3xl mb-8 font-bold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
          💬 Suggestions & Bug Reports
        </h2>

        {viewMode === 'create' ? (
          <div className="space-y-6">
            {submitted ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">✅</div>
                <h3 className="text-2xl font-bold mb-3 text-green-400">Report Submitted!</h3>
                <p className="text-text-secondary">Your {reportType === 'bug' ? 'bug report' : 'suggestion'} has been sent to the developer.</p>
              </div>
            ) : (
              <>
                {/* Report Type */}
                <div>
                  <label className="block mb-3 text-text-secondary font-bold uppercase tracking-wider text-sm">
                    Type:
                  </label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setReportType('bug')}
                      className={`flex-1 px-6 py-3 rounded-full border-2 transition-all duration-300 font-semibold ${
                        reportType === 'bug'
                          ? 'bg-gradient-to-r from-red-500 to-red-400 border-red-500 text-white shadow-bubble-lg scale-105'
                          : 'bg-transparent border-border text-text hover:border-red-500 hover:scale-105'
                      }`}
                    >
                      🐛 Bug Report
                    </button>
                    <button
                      type="button"
                      onClick={() => setReportType('suggestion')}
                      className={`flex-1 px-6 py-3 rounded-full border-2 transition-all duration-300 font-semibold ${
                        reportType === 'suggestion'
                          ? 'bg-gradient-to-r from-green-500 to-green-400 border-green-500 text-white shadow-bubble-lg scale-105'
                          : 'bg-transparent border-border text-text hover:border-green-500 hover:scale-105'
                      }`}
                    >
                      💡 Feature Suggestion
                    </button>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label htmlFor="reportTitle" className="block mb-3 text-text-secondary font-bold uppercase tracking-wider text-sm">
                    Title: *
                  </label>
                  <input
                    type="text"
                    id="reportTitle"
                    placeholder={reportType === 'bug' ? 'Brief description of the bug...' : 'Brief description of your suggestion...'}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-6 py-4 bg-bg border-2 border-border rounded-full text-text text-base font-medium transition-all duration-300 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 focus:shadow-bubble placeholder:text-text-secondary/50"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="reportDescription" className="block mb-3 text-text-secondary font-bold uppercase tracking-wider text-sm">
                    Description: *
                  </label>
                  <textarea
                    id="reportDescription"
                    placeholder={reportType === 'bug' ? 'Describe the bug in detail...' : 'Describe your feature suggestion in detail...'}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={6}
                    className="w-full px-6 py-4 bg-bg border-2 border-border rounded-2xl text-text text-base font-medium transition-all duration-300 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 focus:shadow-bubble placeholder:text-text-secondary/50 resize-none"
                    required
                  />
                </div>

                {/* Steps to Reproduce (only for bugs) */}
                {reportType === 'bug' && (
                  <div>
                    <label htmlFor="reportSteps" className="block mb-3 text-text-secondary font-bold uppercase tracking-wider text-sm">
                      Steps to Reproduce (Optional):
                    </label>
                    <textarea
                      id="reportSteps"
                      placeholder="1. Go to...\n2. Click on...\n3. See error..."
                      value={steps}
                      onChange={(e) => setSteps(e.target.value)}
                      rows={4}
                      className="w-full px-6 py-4 bg-bg border-2 border-border rounded-2xl text-text text-base font-medium transition-all duration-300 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/20 focus:shadow-bubble placeholder:text-text-secondary/50 resize-none"
                    />
                  </div>
                )}

                {/* Error Message */}
                {submitError && (
                  <div className="bg-red-500/20 border-2 border-red-500 rounded-2xl p-4">
                    <p className="text-red-400 text-sm">{submitError}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-col gap-3">
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full py-4 bg-gradient-to-r from-primary to-purple-500 border-none rounded-full text-white text-base font-bold cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-bubble-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? '📤 Sending...' : '📤 Submit Report'}
                  </button>
                  <button
                    onClick={() => handleCopyToClipboard()}
                    className="w-full py-4 bg-gradient-to-br from-bg to-bg-secondary border-2 border-border rounded-full text-text text-base font-semibold cursor-pointer transition-all duration-300 hover:scale-105 hover:border-primary active:scale-95"
                  >
                    {copied ? '✅ Copied to Clipboard!' : '📋 Copy to Clipboard'}
                  </button>
                </div>

                <div className="bg-bg/50 rounded-2xl p-6 border-2 border-border">
                  <p className="text-text-secondary text-sm leading-relaxed">
                    <strong className="text-text">How it works:</strong>
                    <br />
                    • Click <strong>"Submit Report"</strong> to send your report directly to the developer
                    <br />
                    • Reports are sent via email - only the developer can see them
                    <br />
                    • Click <strong>"Copy to Clipboard"</strong> to copy the formatted report for your records
                    <br />
                    • View all your submitted reports using the "View Reports" tab
                    <br />
                    • Reports are also saved locally in your browser
                  </p>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {suggestions.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📭</div>
                <p className="text-text-secondary text-lg">No reports yet. Create your first one!</p>
              </div>
            ) : (
              suggestions.map((report) => (
                <div
                  key={report.id}
                  className="bg-bg p-6 rounded-2xl border-2 border-border"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          report.type === 'bug' 
                            ? 'bg-red-500/20 text-red-400' 
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {report.type === 'bug' ? '🐛 Bug' : '💡 Suggestion'}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          report.status === 'pending' 
                            ? 'bg-yellow-500/20 text-yellow-400' 
                            : report.status === 'reviewed'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {report.status || 'pending'}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-text mb-2">{report.title}</h3>
                      <p className="text-text-secondary text-sm mb-3 whitespace-pre-wrap">{report.description}</p>
                      {report.steps && (
                        <div className="mb-3">
                          <strong className="text-text-secondary text-xs uppercase">Steps to Reproduce:</strong>
                          <p className="text-text-secondary text-sm whitespace-pre-wrap">{report.steps}</p>
                        </div>
                      )}
                      <p className="text-text-secondary text-xs">{formatDate(report.timestamp)}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => handleCopyToClipboard(report)}
                      className="px-4 py-2 bg-bg-secondary border-2 border-border rounded-lg text-text text-sm font-semibold hover:border-primary transition-colors"
                    >
                      {copied ? '✅ Copied!' : '📋 Copy'}
                    </button>
                    <button
                      onClick={() => deleteSuggestion(report.id)}
                      className="px-4 py-2 bg-danger/20 text-danger rounded-lg text-sm font-semibold hover:bg-danger/30 transition-colors"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
